import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Typography, Autocomplete, TextField, Button, CircularProgress,
  Alert, Chip, InputAdornment, Tooltip, IconButton
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import RefreshIcon from '@mui/icons-material/Refresh';
import { loadShippingData, listCountries, calculateShipping, billableKg } from '../utils/shippingCalc';
import { money } from '../utils/quoteMath';

/**
 * Shipping rates come from the same Google Sheet the storefront uses, so a
 * quote can never contradict the price a customer sees at checkout.
 *
 * The computed cost is still editable — a salesperson may have a negotiated
 * rate — but the calculator is the starting point, never a blank field.
 */
const ShippingPicker = ({ shipping, setShipping, totalWeight, customerCountry }) => {
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [country, setCountry] = useState(shipping?.country || '');
  const [weightOverride, setWeightOverride] = useState('');
  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);

  useEffect(() => {
    let dead = false;
    loadShippingData().then((ok) => {
      if (dead) return;
      setReady(!!ok);
      setLoading(false);
    });
    return () => { dead = true; };
  }, []);

  const countries = useMemo(() => (ready ? listCountries() : []), [ready]);

  // Carriers price per whole kilogram with a 1 kg floor, so a 0.07 kg order
  // is still rated at 1 kg. Show both numbers rather than silently rounding.
  const ratedKg = billableKg(weightOverride !== '' ? Number(weightOverride) : totalWeight);

  // Prefill from the customer's country ("Germany" → "Germany - DE") so the
  // salesperson doesn't retype what they just entered above.
  useEffect(() => {
    if (!ready || country || !customerCountry) return;
    const needle = customerCountry.trim().toLowerCase();
    const match = countries.find((c) => {
      if (c.toLowerCase() === needle) return true;
      return c.split(/\s*-\s*/).some((p) => p.trim().toLowerCase() === needle);
    });
    if (match) setCountry(match);
  }, [ready, countries, customerCountry, country]);
  const effectiveWeight = weightOverride !== '' ? Number(weightOverride) : totalWeight;

  const runCalc = useCallback(() => {
    if (!country) return;
    setCalculating(true);
    const r = calculateShipping(country, effectiveWeight);
    setResult(r);
    setCalculating(false);

    // Auto-pick the cheapest rate so the quote is never left without one
    if (r.status === 'ok' && r.options.length) {
      const best = r.options[0];
      setShipping({ carrier: best.carrier, cost: best.price, country, currency: 'EUR' });
    }
  }, [country, effectiveWeight, setShipping]);

  // Recalculate whenever the basket weight changes and a country is already set
  useEffect(() => {
    if (ready && country && totalWeight > 0 && weightOverride === '') runCalc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, country, totalWeight]);

  const pick = (opt) => {
    setShipping({ carrier: opt.carrier, cost: opt.price, country, currency: 'EUR' });
  };

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gap={1.5} py={2}>
        <CircularProgress size={18} /> <Typography variant="caption" color="text.secondary">Loading shipping rates…</Typography>
      </Box>
    );
  }

  if (!ready) {
    return (
      <Alert severity="warning" sx={{ py: 0.5 }}>
        Shipping rate sheet unavailable. Enter the cost manually below.
        <TextField
          size="small" type="number" fullWidth label="Shipping cost" sx={{ mt: 1.5, backgroundColor: 'white' }}
          value={shipping?.cost ?? 0}
          onChange={(e) => setShipping({ cost: Math.max(0, Number(e.target.value) || 0) })}
          InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
        />
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
        <Autocomplete
          size="small" fullWidth options={countries} value={country || null}
          onChange={(_, v) => { setCountry(v || ''); setResult(null); }}
          renderInput={(p) => <TextField {...p} label="Destination country" />}
        />
        <Tooltip title="Recalculate">
          <span>
            <IconButton onClick={runCalc} disabled={!country || calculating}
                        sx={{ border: '1px solid #e5e7eb', borderRadius: 1.5 }}>
              {calculating ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 1.5 }}>
        <TextField
          size="small" type="number" label="Total weight"
          value={weightOverride !== '' ? weightOverride : totalWeight}
          onChange={(e) => setWeightOverride(e.target.value)}
          sx={{ width: 168 }}
          inputProps={{ min: 0, step: 0.01 }}
          InputProps={{ endAdornment: <InputAdornment position="end">kg</InputAdornment> }}
          helperText={
            weightOverride !== ''
              ? `Manual override · charged at ${ratedKg} kg`
              : `Sum of weight × qty · charged at ${ratedKg} kg`
          }
        />
        <Button size="small" startIcon={<CalculateIcon />} onClick={runCalc} disabled={!country}
                variant="outlined" sx={{ textTransform: 'none', borderColor: '#845adf', color: '#845adf', mt: 0.5 }}>
          Calculate
        </Button>
      </Box>

      {result?.status === 'no_route' && (
        <Alert severity="warning" sx={{ py: 0.4, mb: 1.5, fontSize: 12 }}>
          No rate for <strong>{result.country}</strong> at {result.kg || billableKg(effectiveWeight)} kg. Enter the cost manually.
        </Alert>
      )}

      {result?.status === 'ok' && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.75 }}>
            Rates for {result.kg} kg to {result.country}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {result.options.map((o, i) => {
              const selected = shipping?.carrier === o.carrier;
              return (
                <Box key={o.carrier} onClick={() => pick(o)}
                     sx={{
                       display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, cursor: 'pointer',
                       border: '1.5px solid', borderColor: selected ? '#845adf' : '#e5e7eb',
                       backgroundColor: selected ? '#f6f3ff' : 'white', borderRadius: 1.5,
                       transition: 'all .12s', '&:hover': { borderColor: selected ? '#845adf' : '#c4b5fd' },
                     }}>
                  <LocalShippingIcon fontSize="small" sx={{ color: selected ? '#845adf' : '#9ca3af' }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{o.carrier}</Typography>
                  {i === 0 && <Chip label="Cheapest" size="small" sx={{ height: 17, fontSize: 9, backgroundColor: '#dcfce7', color: '#166534' }} />}
                  <Typography variant="body2" sx={{ ml: 'auto', fontWeight: 700 }}>{money(o.price)}</Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      <TextField
        size="small" fullWidth type="number" label="Shipping cost (editable)"
        value={shipping?.cost ?? 0}
        onChange={(e) => setShipping({ cost: Math.max(0, Number(e.target.value) || 0), currency: 'EUR' })}
        InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
        helperText={shipping?.carrier ? `${shipping.carrier} — override if you agreed a different rate` : 'Pick a carrier above, or type a rate'}
      />
    </Box>
  );
};

export default ShippingPicker;
